import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPin } from 'lucide-react';
import type { Incident, SeededFeature } from '@duty/shared';
import { MAPBOX_TOKEN, MAPBOX_STYLES } from '@/config/mapbox';
import { centerForIncident } from '@/config/locations';
import { featuresFor } from '@/incident/spatial';
import { useTheme } from '@/theme/theme';

const KIND_COLOR: Record<string, string> = {
  hydrant: '#3b82f6',
  'static-water': '#14b8a6',
  access: '#f59e0b',
  'local-note': '#eab308',
  exposure: '#ef4444',
};

const LEGEND: { kind: string; label: string }[] = [
  { kind: 'hydrant', label: 'Hydrant' },
  { kind: 'static-water', label: 'Static water' },
  { kind: 'access', label: 'Access / local note' },
  { kind: 'exposure', label: 'Exposure' },
];

function toGeoJSON(features: SeededFeature[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: features.map((f) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [f.coord.lng, f.coord.lat] },
      properties: { kind: f.kind, label: f.label, provenance: f.provenance },
    })),
  };
}

const SOURCE_ID = 'seeded-features';

/**
 * Mapbox tactical map with the seeded living layers (hydrants, static water,
 * access notes, exposures) and the incident marker. Theme-aware; degrades to a
 * labelled placeholder without a token.
 */
export function MapPanel({ incident }: { incident: Incident }) {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  const center = incident.coordinates ?? centerForIncident(incident.id);
  const features = featuresFor(incident);

  function addLayers(map: mapboxgl.Map) {
    if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, { type: 'geojson', data: toGeoJSON(features) });
    }
    if (!map.getLayer(`${SOURCE_ID}-circles`)) {
      map.addLayer({
        id: `${SOURCE_ID}-circles`,
        type: 'circle',
        source: SOURCE_ID,
        paint: {
          'circle-radius': 6,
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#0b0f14',
          'circle-color': [
            'match',
            ['get', 'kind'],
            'hydrant', KIND_COLOR.hydrant,
            'static-water', KIND_COLOR['static-water'],
            'access', KIND_COLOR.access,
            'local-note', KIND_COLOR['local-note'],
            'exposure', KIND_COLOR.exposure,
            '#9ca3af',
          ],
        },
      });
      map.addLayer({
        id: `${SOURCE_ID}-labels`,
        type: 'symbol',
        source: SOURCE_ID,
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 10,
          'text-offset': [0, 1.2],
          'text-anchor': 'top',
          'text-optional': true,
        },
        paint: { 'text-color': '#cbd5e1', 'text-halo-color': '#0b0f14', 'text-halo-width': 1 },
      });
    }
  }

  // Create the map once.
  useEffect(() => {
    if (!MAPBOX_TOKEN || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAPBOX_STYLES[theme],
      center: [center.lng, center.lat],
      zoom: 14.5,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    markerRef.current = new mapboxgl.Marker({ color: '#f45454' }).setLngLat([center.lng, center.lat]).addTo(map);
    map.on('load', () => addLayers(map));
    // setStyle (theme switch) wipes layers — re-add on style.load.
    map.on('style.load', () => addLayers(map));
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recenter + refresh features + marker when the incident changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setCenter([center.lng, center.lat]);
    markerRef.current?.setLngLat([center.lng, center.lat]);
    const src = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    src?.setData(toGeoJSON(features));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center.lng, center.lat, incident.id]);

  // Match the map style to the active theme.
  useEffect(() => {
    mapRef.current?.setStyle(MAPBOX_STYLES[theme]);
  }, [theme]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="panel flex h-full items-center justify-center text-sm text-muted">
        <MapPin size={16} className="mr-2" /> Map unavailable — set VITE_MAPBOX_TOKEN
      </div>
    );
  }

  return (
    <div className="panel relative overflow-hidden">
      <div ref={containerRef} className="h-full w-full" aria-label="Tactical map" />
      <div className="pointer-events-none absolute left-2 top-2 z-10 rounded bg-bg/75 px-2 py-1 text-[10px] text-muted">
        <div className="mb-0.5 font-semibold uppercase tracking-wide">Tactical map · Mapbox</div>
        <div className="flex flex-wrap gap-x-2 gap-y-0.5">
          {LEGEND.map((l) => (
            <span key={l.kind} className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-full" style={{ background: KIND_COLOR[l.kind] }} />
              {l.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
